type LeaveConfirmOptions = {
  isMultiplayer: boolean;
  onConfirm: () => void;
};

export class LeaveConfirmModal {
  private root: HTMLElement;
  private isOpen = false;
  private currentOptions: LeaveConfirmOptions | null = null;
  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.close();
  };

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className =
      'fixed inset-0 z-[80] hidden items-center justify-center bg-wood-coffee/75 backdrop-blur-sm p-4';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', '게임 종료 확인');
    parent.appendChild(this.root);
  }

  mount(): void {
    this.render();
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.close();
    });
  }

  unmount(): void {
    document.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
  }

  open(options: LeaveConfirmOptions): void {
    if (this.isOpen) return;
    this.currentOptions = options;
    this.isOpen = true;
    this.renderBody();
    this.root.classList.remove('hidden');
    this.root.classList.add('flex');
    document.addEventListener('keydown', this.onKeyDown);
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.classList.add('hidden');
    this.root.classList.remove('flex');
    document.removeEventListener('keydown', this.onKeyDown);
  }

  private render(): void {
    this.root.innerHTML = `<div data-role="body"></div>`;
  }

  private renderBody(): void {
    const opts = this.currentOptions;
    if (!opts) return;
    const message = opts.isMultiplayer
      ? '지금 나가면 같은 방의 다른 플레이어에게 영향을 줄 수 있어요. 정말 나가시겠어요?'
      : '진행 중인 게임이 사라져요. 정말 나가시겠어요?';
    const body = this.root.querySelector<HTMLElement>('[data-role="body"]');
    if (!body) return;
    body.innerHTML = `
      <div class="rounded-2xl bg-wood-paper ring-1 ring-wood-sand max-w-sm w-full p-6 shadow-xl flex flex-col gap-4">
        <h2 class="text-xl font-extrabold text-wood-ink">게임 종료</h2>
        <p class="text-body text-wood-coffee leading-relaxed">${escapeHtml(message)}</p>
        <div class="flex gap-2 mt-2">
          <button type="button" data-role="cancel" class="flex-1 rounded-xl bg-wood-paper ring-1 ring-wood-sand hover:bg-wood-cream text-wood-ink py-3 font-semibold transition">취소</button>
          <button type="button" data-role="confirm" class="flex-1 rounded-xl bg-rose-500/90 hover:bg-rose-400 text-white py-3 font-bold shadow-md shadow-rose-500/20 active:scale-[0.98] transition">나가기</button>
        </div>
      </div>
    `;
    body
      .querySelector<HTMLButtonElement>('[data-role="cancel"]')
      ?.addEventListener('click', () => this.close());
    body
      .querySelector<HTMLButtonElement>('[data-role="confirm"]')
      ?.addEventListener('click', () => {
        const onConfirm = opts.onConfirm;
        this.close();
        onConfirm();
      });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
